// API client for the provider mock's operator API (/mock-admin/*).
//
// Same-origin only: every request goes to the relative base
// `/mock-admin` (nginx / the vite dev server proxy it to
// third-party-mock-service) with `credentials: 'same-origin'`, so the
// HttpOnly, SameSite=Strict, Path=/mock-admin session cookie rides along
// and nothing else does. Every mutating request carries
// `X-Mock-Console: 1` — with SameSite=Strict and no CORS preflight answer
// that header is the console's CSRF boundary.
//
// The client refuses to send anything when the configured base fails
// the sandbox guard's configuration check.

import { evaluateConfig, SANDBOX_API_BASE } from "./sandboxGuard.ts";

/** Build-time API base. Anything but `/mock-admin` blocks the app. */
export const API_BASE: string = import.meta.env.VITE_MOCK_API_BASE || SANDBOX_API_BASE;

export const CONSOLE_HEADER = "X-Mock-Console";

export class APIError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

function configOk(): void {
  const host = typeof window !== "undefined" ? window.location.hostname : "localhost";
  const v = evaluateConfig(host, API_BASE);
  if (!v.ok) throw new APIError(0, "SANDBOX_GUARD", v.message);
}

/** URL of an operator-API path, e.g. url("/export/audit.csv?day=…"). */
export function apiUrl(path: string): string {
  return `${API_BASE.replace(/\/+$/, "")}${path}`;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  configOk();
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json", ...opts.headers };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET") headers[CONSOLE_HEADER] = "1";
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method,
      headers,
      credentials: "same-origin",
      cache: "no-store",
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error)?.name === "AbortError") throw e;
    throw new APIError(0, "NETWORK", "The provider mock is unreachable. Check that third-party-mock-service is running.");
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }
  if (!res.ok) {
    const err = (json ?? {}) as { code?: string; message?: string };
    throw new APIError(res.status, err.code ?? `HTTP_${res.status}`, err.message ?? `The mock answered HTTP ${res.status}.`);
  }
  return json as T;
}

/** A leading-? query string; empty values are dropped. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

/** The operator-facing message of any failure (the server's `message`). */
export function errorMessage(e: unknown): string {
  if (e instanceof APIError) return e.message;
  if (e instanceof Error && e.message) return e.message;
  return "Something went wrong.";
}

export function newRequestId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  const id = c?.randomUUID ? c.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return `console_${id}`;
}
